import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Paper,
  Typography,
  Box,
  Chip,
  LinearProgress,
  CircularProgress,
  Alert,
  Stack,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  IconButton,
  TablePagination,
  Snackbar,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Switch,
  Divider,
} from "@mui/material";
import { Search, MoreVert } from "@mui/icons-material";
import { useQuery } from "@apollo/client";
import { RECIPE_FILES } from "../../graphql/AICopilotQueries";

interface RecipeFile {
  name: string;
  title: string;
  size: number;
  lastModified: string;
}

interface BlueprintData {
  name: string;
  title: string;
  diseaseGroup: string;
  modifiedBy: string;
  numberOfEdits: number;
  successRate: number;
  lastModified: string;
  status: "active" | "draft" | "archived";
}

const BlueprintDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [diseaseGroupFilter, setDiseaseGroupFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showApprovalToast, setShowApprovalToast] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedBlueprint, setSelectedBlueprint] =
    useState<BlueprintData | null>(null);
  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState("");
  const [productionVersions, setProductionVersions] = useState<{
    [key: string]: string;
  }>({});

  // Professional headshot images for medical professionals
  const getUserProfileImage = (name: string) => {
    const profileImages: { [key: string]: string } = {
      "Sarah Chen": "https://randomuser.me/api/portraits/women/1.jpg",
      "Michael Rodriguez": "https://randomuser.me/api/portraits/men/2.jpg",
      "Emily Johnson": "https://randomuser.me/api/portraits/women/3.jpg",
      "James Wilson": "https://randomuser.me/api/portraits/men/4.jpg",
      "Lisa Anderson": "https://randomuser.me/api/portraits/women/5.jpg",
      "Robert Garcia": "https://randomuser.me/api/portraits/men/6.jpg",
      "Jennifer Lee": "https://randomuser.me/api/portraits/women/7.jpg",
      "David Thompson": "https://randomuser.me/api/portraits/men/8.jpg",
      "Rachel Brown": "https://randomuser.me/api/portraits/women/9.jpg",
      "Kevin Park": "https://randomuser.me/api/portraits/men/10.jpg",
      "John Smith": "https://randomuser.me/api/portraits/men/11.jpg",
    };

    return (
      profileImages[name] || "https://randomuser.me/api/portraits/men/12.jpg"
    );
  };

  // Handler functions for actions menu and versions modal
  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    blueprint: BlueprintData,
  ) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedBlueprint(blueprint);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    // Don't clear selectedBlueprint here - we need it for the versions modal
  };

  const handleVersionsClick = () => {
    if (selectedBlueprint) {
      const versions = getVersionsData(selectedBlueprint);
      const productionVersion = versions.find((v) => v.inProduction);
      setSelectedVersion(
        productionVersion?.version ||
          `v1.${Math.max(selectedBlueprint.numberOfEdits, 0)}`,
      );
    }
    setShowVersionsModal(true);
    handleMenuClose();
  };

  const handleVersionsModalClose = () => {
    setShowVersionsModal(false);
    setSelectedVersion("");
    setSelectedBlueprint(null); // Clear the blueprint when modal closes
  };

  const handleSetProduction = (versionToSet: string) => {
    if (selectedBlueprint) {
      setProductionVersions((prev) => ({
        ...prev,
        [selectedBlueprint.name]: versionToSet,
      }));
    }
  };

  // Generate versions data based on the blueprint's edit count
  const getVersionsData = (blueprint: BlueprintData | null) => {
    if (!blueprint) return [];

    const versions = [];
    const totalVersions = Math.max(blueprint.numberOfEdits + 1, 1); // +1 for the original version, minimum 1

    // Get the production version for this blueprint, default to latest
    const currentProductionVersion =
      productionVersions[blueprint.name] || `v1.${totalVersions - 1}`;

    // Generate versions from newest to oldest
    for (let i = totalVersions; i >= 1; i--) {
      const versionNumber = `v1.${i - 1}`;
      const isProduction = versionNumber === currentProductionVersion;
      const daysAgo = totalVersions - i;

      // Generate a date going backwards from today
      const date = new Date();
      date.setDate(date.getDate() - daysAgo);

      const changeDescriptions = [
        "Updated dose constraints and optimization objectives",
        "Modified organ structure priorities and tolerances",
        "Adjusted beam geometry and planning parameters",
        "Updated target coverage requirements",
        "Modified normal tissue constraints",
        "Refined optimization algorithm settings",
        "Updated planning objectives and priorities",
        "Adjusted dose calculation parameters",
      ];

      versions.push({
        version: versionNumber,
        date: date
          .toLocaleDateString("en-US", {
            month: "short",
            day: "2-digit",
            year: "numeric",
          })
          .replace(/\s/g, "-")
          .replace(/,/g, ""),
        time: `${Math.floor(Math.random() * 12) + 1}:${String(Math.floor(Math.random() * 60)).padStart(2, "0")} ${Math.random() > 0.5 ? "AM" : "PM"}`,
        author:
          i === totalVersions
            ? blueprint.modifiedBy
            : i === 1
              ? "System" // Original version created by system
              : [
                  "Sarah Chen",
                  "Michael Rodriguez",
                  "Emily Johnson",
                  "James Wilson",
                  "Lisa Anderson",
                ][Math.floor(Math.random() * 5)],
        changes:
          i === 1
            ? "Original model creation"
            : changeDescriptions[
                Math.floor(Math.random() * changeDescriptions.length)
              ],
        inProduction: isProduction,
      });
    }

    return versions;
  };

  // Check if we came from plan approval
  useEffect(() => {
    if (location.state?.planApproved) {
      setShowApprovalToast(true);
      // Clear the state to prevent showing toast on refresh
      navigate("/blueprint", { replace: true });
    }
  }, [location.state, navigate]);

  const { data, loading, error } = useQuery(RECIPE_FILES);

  // Helper function to generate random date within last 2 weeks
  const getRandomDateInLastTwoWeeks = () => {
    const now = new Date();
    const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
    const randomTime =
      twoWeeksAgo.getTime() +
      // eslint-disable-next-line react-hooks/purity
      Math.random() * (now.getTime() - twoWeeksAgo.getTime());
    return new Date(randomTime).toISOString();
  };

  // Mock data for ten custom blueprints with success rates under 80%
  const mockBlueprints: BlueprintData[] = [
    {
      name: "ProstateNodes.toml",
      title: "ProstateNodes",
      diseaseGroup: "Prostate",
      modifiedBy: "Sarah Chen",
      numberOfEdits: 6,
      successRate: 48,
      lastModified: new Date().toISOString(), // Current time for ProstateNodes
      status: "active",
    },
    {
      name: "BreastLtVMAT.toml",
      title: "BreastLtVMAT",
      diseaseGroup: "Breast",
      modifiedBy: "Michael Rodriguez",
      numberOfEdits: 3,
      successRate: 49.2,
      lastModified: getRandomDateInLastTwoWeeks(),
      status: "draft",
    },
    {
      name: "LungSRTVMATLt.toml",
      title: "LungSRTVMATLt",
      diseaseGroup: "Lung",
      modifiedBy: "Emily Johnson",
      numberOfEdits: 7,
      successRate: 49.3,
      lastModified: getRandomDateInLastTwoWeeks(),
      status: "active",
    },
    {
      name: "HNLtHigh.toml",
      title: "HNLtHigh",
      diseaseGroup: "Head & Neck",
      modifiedBy: "James Wilson",
      numberOfEdits: 2,
      successRate: 51.1,
      lastModified: getRandomDateInLastTwoWeeks(),
      status: "draft",
    },
    {
      name: "AbdoHigh.toml",
      title: "AbdoHigh",
      diseaseGroup: "Abdomen",
      modifiedBy: "Lisa Anderson",
      numberOfEdits: 1,
      successRate: 52.4,
      lastModified: getRandomDateInLastTwoWeeks(),
      status: "active",
    },
    {
      name: "BrainHighLow.toml",
      title: "BrainHighLow",
      diseaseGroup: "CNS",
      modifiedBy: "Robert Garcia",
      numberOfEdits: 5,
      successRate: 53.5,
      lastModified: getRandomDateInLastTwoWeeks(),
      status: "active",
    },
    {
      name: "PelvisHigh.toml",
      title: "PelvisHigh",
      diseaseGroup: "Pelvis",
      modifiedBy: "Jennifer Lee",
      numberOfEdits: 4,
      successRate: 76.1,
      lastModified: getRandomDateInLastTwoWeeks(),
      status: "active",
    },
    {
      name: "EsophagusHigh.toml",
      title: "EsophagusHigh",
      diseaseGroup: "Thorax",
      modifiedBy: "David Thompson",
      numberOfEdits: 0,
      successRate: 77.1,
      lastModified: getRandomDateInLastTwoWeeks(),
      status: "draft",
    },
    {
      name: "BladderHigh.toml",
      title: "BladderHigh",
      diseaseGroup: "Bladder",
      modifiedBy: "Rachel Brown",
      numberOfEdits: 3,
      successRate: 78.1,
      lastModified: getRandomDateInLastTwoWeeks(),
      status: "active",
    },
    {
      name: "PelvisLow.toml",
      title: "PelvisLow",
      diseaseGroup: "Pelvis",
      modifiedBy: "Kevin Park",
      numberOfEdits: 2,
      successRate: 78.2,
      lastModified: getRandomDateInLastTwoWeeks(),
      status: "active",
    },
  ];

  const handleRowClick = (blueprint: BlueprintData) => {
    navigate(`/blueprint/${encodeURIComponent(blueprint.name)}`);
  };

  const getSuccessRateColor = (rate: number) => {
    if (rate >= 75) return "success";
    if (rate >= 50) return "warning";
    return "error";
  };

  const getStatusColor = (status: string): "success" | "default" => {
    switch (status) {
      case "active":
        return "success";
      case "draft":
        return "default";
      case "archived":
        return "default";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Failed to load blueprints: {error.message}
        </Alert>
      </Box>
    );
  }

  // Get recipes from API and convert to blueprint format
  const recipes = data?.recipeFiles || [];
  const apiBlueprints = recipes.map(
    (recipe: RecipeFile): BlueprintData => ({
      name: recipe.name,
      title: recipe.title || recipe.name.replace(/\.toml$/, ""),
      diseaseGroup: "General",
      modifiedBy: "John Smith",
      // eslint-disable-next-line react-hooks/purity
      numberOfEdits: Math.floor(Math.random() * 8),
      // eslint-disable-next-line react-hooks/purity
      successRate: Math.round(Math.random() * 90) / 10 + 91, // 91-100% for API recipes
      lastModified: getRandomDateInLastTwoWeeks(),
      status: "active" as const,
    }),
  );

  // Combine mock blueprints with API recipes
  const blueprints = [...mockBlueprints, ...apiBlueprints];

  // Sort blueprints by success rate (low to high)
  const sortedBlueprints = [...blueprints].sort(
    (a, b) => a.successRate - b.successRate,
  );

  // Filter blueprints based on search and filters
  const filteredBlueprints = sortedBlueprints.filter(
    (blueprint: BlueprintData) => {
      const matchesSearch =
        blueprint.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        blueprint.diseaseGroup
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        blueprint.modifiedBy.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesDiseaseGroup =
        diseaseGroupFilter === "all" ||
        blueprint.diseaseGroup === diseaseGroupFilter;

      const matchesStatus =
        statusFilter === "all" || blueprint.status === statusFilter;

      return matchesSearch && matchesDiseaseGroup && matchesStatus;
    },
  );

  // Get unique disease groups for filter
  const diseaseGroups = Array.from(
    new Set(sortedBlueprints.map((b: BlueprintData) => b.diseaseGroup)),
  );

  // Pagination handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get paginated data
  const paginatedBlueprints = filteredBlueprints.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {/* Container with 80% width and centered */}
      <Box
        sx={{
          width: "80%",
          mx: "auto",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box sx={{ p: 3, pb: 2 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              fontSize: "24px",
              color: "text.primary",
              letterSpacing: "-0.025em",
              marginTop: "20px",
            }}
          >
            Blueprint Management
          </Typography>
        </Box>

        {/* Filters */}
        <Box sx={{ px: 3, pb: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              placeholder="Search blueprints..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{
                minWidth: 300,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  backgroundColor: "#ffffff",
                  border: "1px solid #e5e7eb",
                  fontSize: "14px",
                  "&:hover": {
                    border: "1px solid #d1d5db",
                  },
                  "&.Mui-focused": {
                    border: "1px solid #3b82f6",
                    backgroundColor: "#ffffff",
                    boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
                  },
                  "& fieldset": {
                    border: "none",
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" sx={{ color: "#9ca3af" }} />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                value={diseaseGroupFilter}
                onChange={(e) => setDiseaseGroupFilter(e.target.value)}
                displayEmpty
                sx={{
                  borderRadius: "6px",
                  backgroundColor: "#ffffff",
                  border: "1px solid #e5e7eb",
                  fontSize: "14px",
                  "&:hover": {
                    border: "1px solid #d1d5db",
                  },
                  "&.Mui-focused": {
                    border: "1px solid #3b82f6",
                    backgroundColor: "#ffffff",
                    boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "& .MuiSelect-select": {
                    padding: "8px 14px",
                    fontSize: "14px",
                    color: "#374151",
                  },
                }}
                renderValue={(value) =>
                  value === "all" ? "Disease Group" : value
                }
              >
                <MenuItem value="all">All Groups</MenuItem>
                {diseaseGroups.map((group) => (
                  <MenuItem key={group} value={group}>
                    {group}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                displayEmpty
                sx={{
                  borderRadius: "6px",
                  backgroundColor: "#ffffff",
                  border: "1px solid #e5e7eb",
                  fontSize: "14px",
                  "&:hover": {
                    border: "1px solid #d1d5db",
                  },
                  "&.Mui-focused": {
                    border: "1px solid #3b82f6",
                    backgroundColor: "#ffffff",
                    boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "& .MuiSelect-select": {
                    padding: "8px 14px",
                    fontSize: "14px",
                    color: "#374151",
                  },
                }}
                renderValue={(value) => (value === "all" ? "Status" : value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Box>

        {/* Table */}
        <TableContainer
          component={Paper}
          sx={{
            flex: 1,
            mx: 3,
            mb: 3,
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
            borderRadius: 2,
            overflow: "auto",
            maxHeight: "calc(100vh - 200px)",
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    backgroundColor: "#F1F5F9",
                  }}
                >
                  Blueprint
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    backgroundColor: "#F1F5F9",
                  }}
                >
                  Disease Group
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    backgroundColor: "#F1F5F9",
                  }}
                >
                  Modified By
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    backgroundColor: "#F1F5F9",
                  }}
                >
                  Last Modified
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    backgroundColor: "#F1F5F9",
                  }}
                >
                  Edits
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    backgroundColor: "#F1F5F9",
                  }}
                >
                  Approval Rate
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    backgroundColor: "#F1F5F9",
                  }}
                >
                  Status
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    backgroundColor: "#F1F5F9",
                    textAlign: "right",
                    paddingRight: 3,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedBlueprints.map((blueprint: BlueprintData) => (
                <TableRow
                  key={blueprint.name}
                  hover
                  onClick={() => handleRowClick(blueprint)}
                  sx={{
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#f8f9fa",
                    },
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  <TableCell sx={{ py: 2 }}>
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      sx={{ lineHeight: 1.2 }}
                    >
                      {blueprint.title}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 2 }}>
                    <Typography variant="body2" sx={{ color: "text.primary" }}>
                      {blueprint.diseaseGroup}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 2 }}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Avatar
                        src={getUserProfileImage(blueprint.modifiedBy)}
                        alt={blueprint.modifiedBy}
                        sx={{
                          width: 32,
                          height: 32,
                        }}
                      />
                      <Typography variant="body2">
                        {blueprint.modifiedBy}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 2 }}>
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{ lineHeight: 1.2 }}
                      >
                        {new Date(blueprint.lastModified)
                          .toLocaleDateString("en-US", {
                            month: "short",
                            day: "2-digit",
                            year: "numeric",
                          })
                          .replace(/\s/g, "-")
                          .replace(/,/g, "")}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ lineHeight: 1.2, fontSize: "0.75rem" }}
                      >
                        {new Date(blueprint.lastModified).toLocaleTimeString(
                          "en-US",
                          {
                            hour: "numeric",
                            minute: "2-digit",
                            hour12: true,
                          },
                        )}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 2 }}>
                    <Typography variant="body2" color="text.primary">
                      {blueprint.numberOfEdits}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ minWidth: 80 }}>
                        <Typography
                          variant="body2"
                          component="div"
                          fontWeight="medium"
                        >
                          {blueprint.successRate}%
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={blueprint.successRate}
                          color={getSuccessRateColor(blueprint.successRate)}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: "#f3f4f6",
                            "& .MuiLinearProgress-bar": {
                              borderRadius: 3,
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 2 }}>
                    <Chip
                      label={blueprint.status}
                      size="small"
                      color={getStatusColor(blueprint.status)}
                      sx={{
                        textTransform: "capitalize",
                        borderRadius: "50px",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        minWidth: 70,
                        height: "24px",
                        backgroundColor:
                          blueprint.status === "draft" ? "#EEEBFB" : "default",
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{ py: 2, textAlign: "right", paddingRight: 3 }}
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuClick(e, blueprint)}
                      sx={{ color: "#6b7280" }}
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  colSpan={8}
                  count={filteredBlueprints.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{
                    border: 0,
                    "& .MuiTablePagination-toolbar": {
                      paddingLeft: 2,
                      paddingRight: 2,
                    },
                  }}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Box>

      {/* Success Toast */}
      <Snackbar
        open={showApprovalToast}
        autoHideDuration={3000}
        onClose={() => setShowApprovalToast(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowApprovalToast(false)}
          severity="success"
          variant="filled"
          sx={{
            width: "100%",
            backgroundColor: "primary.main",
            color: "primary.contrastText",
          }}
        >
          You have approved the blueprint and the changes have been saved
        </Alert>
      </Snackbar>

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleVersionsClick}>
          <Typography variant="body2">Versions</Typography>
        </MenuItem>
      </Menu>

      {/* Versions Modal */}
      <Dialog
        open={showVersionsModal}
        onClose={handleVersionsModalClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">
            Version History - {selectedBlueprint?.title}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {!selectedBlueprint ? (
            <Typography>Loading blueprint data...</Typography>
          ) : (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Model:{" "}
                {selectedBlueprint?.title ||
                  selectedBlueprint?.name ||
                  "Unknown"}{" "}
                ({selectedBlueprint?.numberOfEdits || 0} edits)
              </Typography>

              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Select Version</InputLabel>
                <Select
                  value={selectedVersion}
                  label="Select Version"
                  onChange={(e) => setSelectedVersion(e.target.value)}
                >
                  {(() => {
                    const versions = getVersionsData(selectedBlueprint);
                    return versions.map((version) => (
                      <MenuItem key={version.version} value={version.version}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                          }}
                        >
                          <Typography>{version.version}</Typography>
                          {version.inProduction && (
                            <Chip
                              label="Production"
                              size="small"
                              color="success"
                              sx={{ height: 18, fontSize: "0.65rem", ml: 1 }}
                            />
                          )}
                        </Box>
                      </MenuItem>
                    ));
                  })()}
                </Select>
              </FormControl>

              {/* Version Details */}
              {selectedVersion && (
                <Box sx={{ p: 2, backgroundColor: "#f8f9fa", borderRadius: 1 }}>
                  {(() => {
                    const versionData = getVersionsData(selectedBlueprint).find(
                      (v) => v.version === selectedVersion,
                    );
                    if (!versionData) return null;

                    return (
                      <>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            mb: 2,
                          }}
                        >
                          <Typography variant="h6">
                            {versionData.version}
                          </Typography>
                          {versionData.inProduction && (
                            <Chip
                              label="Currently in Production"
                              size="small"
                              color="success"
                              sx={{ height: 20, fontSize: "0.7rem" }}
                            />
                          )}
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Created: {versionData.date} at {versionData.time}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          Author: {versionData.author}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                          <strong>Changes:</strong> {versionData.changes}
                        </Typography>

                        {!versionData.inProduction && (
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() =>
                              handleSetProduction(versionData.version)
                            }
                            sx={{ mt: 1 }}
                          >
                            Set as Production
                          </Button>
                        )}
                      </>
                    );
                  })()}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleVersionsModalClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BlueprintDashboard;
